/**
 * Supabase Document Workflow Integration
 *
 * This file provides utilities for persisting document templates and instances to Supabase
 */
import { supabase } from '@/lib/supabase-client';
/**
 * Create a new document template
 */
export async function createDocumentTemplate(template) {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    // Extract elements from template content
    const extractedElements = extractTemplateElements(template.templateContent);
    const { data, error } = await supabase
        .from('document_templates')
        .insert({
        name: template.name,
        description: template.description,
        category: template.category,
        template_content: template.templateContent,
        field_configurations: template.fieldConfigurations,
        extracted_elements: extractedElements,
        product_id: template.productId,
        author_id: user?.id,
        is_active: true,
        is_published: false
    })
        .select()
        .single();
    if (error)
        throw error;
    return data;
}
/**
 * Get all active document templates
 */
export async function getDocumentTemplates(filters) {
    let query = supabase
        .from('document_templates')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });
    if (filters?.category) {
        query = query.eq('category', filters.category);
    }
    if (filters?.productId) {
        query = query.eq('product_id', filters.productId);
    }
    if (filters?.publishedOnly) {
        query = query.eq('is_published', true);
    }
    const { data, error } = await query;
    if (error)
        throw error;
    return data;
}
/**
 * Get a single document template by ID
 */
export async function getDocumentTemplate(templateId) {
    const { data, error } = await supabase
        .from('document_templates')
        .select('*')
        .eq('id', templateId)
        .single();
    if (error)
        throw error;
    return data;
}
/**
 * Update a document template
 */
export async function updateDocumentTemplate(templateId, updates) {
    // If template_content is being updated, re-extract elements
    if (updates.template_content) {
        updates.extracted_elements = extractTemplateElements(updates.template_content);
    }
    const { data, error } = await supabase
        .from('document_templates')
        .update(updates)
        .eq('id', templateId)
        .select()
        .single();
    if (error)
        throw error;
    return data;
}
/**
 * Publish a document template
 */
export async function publishDocumentTemplate(templateId) {
    const { data, error } = await supabase
        .from('document_templates')
        .update({
        is_published: true,
        published_at: new Date().toISOString()
    })
        .eq('id', templateId)
        .select()
        .single();
    if (error)
        throw error;
    return data;
}
/**
 * Delete a document template
 */
export async function deleteDocumentTemplate(templateId) {
    const { error } = await supabase
        .from('document_templates')
        .delete()
        .eq('id', templateId);
    if (error)
        throw error;
    return { success: true };
}
/**
 * Create a new document instance
 */
export async function createDocumentInstance(document) {
    const { data, error } = await supabase
        .from('document_instances')
        .insert({
        template_id: document.templateId,
        template_version: document.templateVersion,
        offering_id: document.offeringId,
        investor_id: document.investorId,
        field_values: document.fieldValues || {},
        signatures: {},
        status: 'draft'
    })
        .select()
        .single();
    if (error)
        throw error;
    return data;
}
/**
 * Get document instances for current user
 */
export async function getMyDocumentInstances(filters) {
    let query = supabase
        .from('document_instances')
        .select(`
      *,
      template:document_templates(name, description, category)
    `)
        .order('created_at', { ascending: false });
    if (filters?.status) {
        query = query.eq('status', filters.status);
    }
    if (filters?.offeringId) {
        query = query.eq('offering_id', filters.offeringId);
    }
    const { data, error } = await query;
    if (error)
        throw error;
    return data;
}
/**
 * Get all document instances (for compliance/admin)
 */
export async function getAllDocumentInstances(filters) {
    let query = supabase
        .from('document_instances')
        .select(`
      *,
      template:document_templates(name, description, category),
      investor:profiles(full_name, email)
    `)
        .order('created_at', { ascending: false });
    if (filters?.status) {
        query = query.eq('status', filters.status);
    }
    if (filters?.offeringId) {
        query = query.eq('offering_id', filters.offeringId);
    }
    if (filters?.investorId) {
        query = query.eq('investor_id', filters.investorId);
    }
    const { data, error } = await query;
    if (error)
        throw error;
    return data;
}
/**
 * Get a single document instance
 */
export async function getDocumentInstance(documentId) {
    const { data, error } = await supabase
        .from('document_instances')
        .select(`
      *,
      template:document_templates(*)
    `)
        .eq('id', documentId)
        .single();
    if (error)
        throw error;
    return data;
}
/**
 * Update document instance (save draft)
 */
export async function updateDocumentInstance(documentId, updates) {
    const { data, error } = await supabase
        .from('document_instances')
        .update({
        field_values: updates.fieldValues,
        signatures: updates.signatures,
        status: updates.status,
        compiled_content: updates.compiledContent
    })
        .eq('id', documentId)
        .select()
        .single();
    if (error)
        throw error;
    // Log activity
    await logDocumentActivity({
        documentInstanceId: documentId,
        activityType: 'updated',
        activityDescription: 'Document updated',
        changes: updates
    });
    return data;
}
/**
 * Submit document for review
 */
export async function submitDocumentForReview(documentId) {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
        .from('document_instances')
        .update({
        status: 'pending_compliance',
        submitted_at: new Date().toISOString(),
        submitted_by: user?.id
    })
        .eq('id', documentId)
        .select()
        .single();
    if (error)
        throw error;
    await logDocumentActivity({
        documentInstanceId: documentId,
        activityType: 'submitted',
        activityDescription: 'Document submitted for compliance review'
    });
    return data;
}
/**
 * Approve document
 */
export async function approveDocument(documentId) {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
        .from('document_instances')
        .update({
        status: 'completed',
        approved_at: new Date().toISOString(),
        approved_by: user?.id
    })
        .eq('id', documentId)
        .select()
        .single();
    if (error)
        throw error;
    await logDocumentActivity({
        documentInstanceId: documentId,
        activityType: 'approved',
        activityDescription: 'Document approved by compliance'
    });
    return data;
}
/**
 * Reject document
 */
export async function rejectDocument(documentId, reason) {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
        .from('document_instances')
        .update({
        status: 'rejected',
        rejected_at: new Date().toISOString(),
        rejected_by: user?.id,
        rejection_reason: reason
    })
        .eq('id', documentId)
        .select()
        .single();
    if (error)
        throw error;
    await logDocumentActivity({
        documentInstanceId: documentId,
        activityType: 'rejected',
        activityDescription: `Document rejected: ${reason}`
    });
    return data;
}
/**
 * Save a signature
 */
export async function saveSignature(signature) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user)
        throw new Error('Not authenticated');
    const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', user.id)
        .single();
    const { data, error } = await supabase
        .from('document_signatures')
        .upsert({
        document_instance_id: signature.documentInstanceId,
        field_name: signature.fieldName,
        signature_type: signature.signatureType,
        signature_data: signature.signatureData,
        signed_by: user.id,
        signer_email: profile?.email || user.email || '',
        signer_full_name: profile?.full_name || 'Unknown',
        signed_at: new Date().toISOString()
    }, {
        onConflict: 'document_instance_id,field_name'
    })
        .select()
        .single();
    if (error)
        throw error;
    await logDocumentActivity({
        documentInstanceId: signature.documentInstanceId,
        activityType: 'signed',
        activityDescription: `Signature added for field: ${signature.fieldName}`
    });
    return data;
}
/**
 * Get signatures for a document
 */
export async function getDocumentSignatures(documentId) {
    const { data, error } = await supabase
        .from('document_signatures')
        .select('*')
        .eq('document_instance_id', documentId)
        .order('signed_at', { ascending: true });
    if (error)
        throw error;
    return data;
}
// =====================================================
// ACTIVITY LOG
// =====================================================
/**
 * Log document activity
 */
export async function logDocumentActivity(activity) {
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
        .from('document_activity_log')
        .insert({
        document_instance_id: activity.documentInstanceId,
        activity_type: activity.activityType,
        activity_description: activity.activityDescription,
        performed_by: user?.id,
        performed_by_email: user?.email,
        changes: activity.changes
    });
    if (error)
        console.error('Failed to log activity:', error);
}
/**
 * Get activity log for a document
 */
export async function getDocumentActivityLog(documentId) {
    const { data, error } = await supabase
        .from('document_activity_log')
        .select(`
      *,
      user:profiles(full_name, email)
    `)
        .eq('document_instance_id', documentId)
        .order('created_at', { ascending: false });
    if (error)
        throw error;
    return data;
}
// =====================================================
// FILE STORAGE
// =====================================================
/**
 * Upload generated PDF to storage
 */
export async function uploadDocumentPDF(documentId, pdfBlob, fileName) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user)
        throw new Error('Not authenticated');
    const filePath = `${user.id}/${documentId}/${fileName}`;
    const { data, error } = await supabase.storage
        .from('document-pdfs')
        .upload(filePath, pdfBlob, {
        contentType: 'application/pdf',
        upsert: true
    });
    if (error)
        throw error;
    // Get public URL
    const { data: urlData } = supabase.storage
        .from('document-pdfs')
        .getPublicUrl(filePath);
    // Update document instance with PDF URL
    await supabase
        .from('document_instances')
        .update({ generated_pdf_url: urlData.publicUrl })
        .eq('id', documentId);
    return urlData.publicUrl;
}
/**
 * Upload signature image to storage
 */
export async function uploadSignatureImage(signatureBlob, fileName) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user)
        throw new Error('Not authenticated');
    const filePath = `${user.id}/${Date.now()}-${fileName}`;
    const { data, error } = await supabase.storage
        .from('document-signatures')
        .upload(filePath, signatureBlob, {
        contentType: 'image/png',
        upsert: false
    });
    if (error)
        throw error;
    const { data: urlData } = supabase.storage
        .from('document-signatures')
        .getPublicUrl(filePath);
    return urlData.publicUrl;
}
// =====================================================
// TEMPLATE ELEMENT EXTRACTION
// =====================================================
/**
 * Extract all elements from template content
 * This matches the logic used in TemplateLibrary.tsx for consistency
 */
export function extractTemplateElements(templateContent) {
    // Extract YAML section first - we don't count elements in YAML
    const yamlMatch = templateContent.match(/^---\s*\n([\s\S]*?)\n---/);
    const templateBodyOnly = yamlMatch ? templateContent.substring(yamlMatch[0].length) : templateContent;
    // Extract variables {{}}
    const variablePattern = /\{\{([^}]+)\}\}/g;
    const variableMatches = [...templateBodyOnly.matchAll(variablePattern)];
    const variables = [...new Set(variableMatches
            .map(m => m[1].trim())
            .filter(v => !v.startsWith('@') && !v.includes('{{')))];
    // Extract placeholders [] (excluding conditionals and cross-references)
    const placeholderPattern = /\[([^\]]+)\]/g;
    const placeholderMatches = [...templateBodyOnly.matchAll(placeholderPattern)];
    const placeholders = [...new Set(placeholderMatches
            .map(m => m[1].trim())
            .filter(p => !p.includes('{{') && !p.includes('|')))];
    // Extract conditionals [{{}}]
    const conditionalPattern = /\[\{\{([^}]+)\}\}/g;
    const conditionalMatches = [...templateBodyOnly.matchAll(conditionalPattern)];
    const conditionals = [...new Set(conditionalMatches.map(m => m[1].trim()))];
    // Extract cross-references |ref|
    const crossRefPattern = /\|([^|]+)\|/g;
    const crossRefMatches = [...templateBodyOnly.matchAll(crossRefPattern)];
    const crossReferences = [...new Set(crossRefMatches.map(m => m[1].trim()))];
    // Check for auto-numbering (l., ll., lll., etc.)
    const autoNumbering = /^l+\.\s/m.test(templateBodyOnly);
    // Extract date variables (@today, @date, etc.)
    const dateVarPattern = /@(\w+)/g;
    const dateVarMatches = [...templateBodyOnly.matchAll(dateVarPattern)];
    const dateVariables = [...new Set(dateVarMatches.map(m => '@' + m[1]))];
    return {
        variables,
        placeholders,
        conditionals,
        crossReferences,
        autoNumbering,
        dateVariables
    };
}
